import React, { useState } from 'react';
import { View, Text, ScrollView, Pressable, TextInput, Modal, KeyboardAvoidingView, Platform } from 'react-native';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  Plus,
  Star,
  DollarSign,
  TrendingUp,
  UserPlus,
  Rocket,
  Megaphone,
  Newspaper,
  Clock,
  ExternalLink,
  X,
  ChevronDown,
  Link,
  Check,
  Globe,
  FolderOpen,
  Lock,
  Eye,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { StartupUpdate, UpdateVisibility } from '@/lib/state/inverio-store';
import { isWeb } from '@/lib/platform';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import { SectionDivider } from '@/components/SectionDivider';
import AnimatedPressable from '@/components/AnimatedPressable';

// Standard shadow styles
const cardShadow = {
  shadowColor: '#000',
  shadowOpacity: 0.05,
  shadowRadius: 8,
  shadowOffset: { width: 0, height: 2 },
  elevation: 2,
};

type UpdateType = StartupUpdate['type'];

const UPDATE_TYPES: { type: UpdateType; label: string; description: string }[] = [
  { type: 'milestone', label: 'Milestone', description: 'Major achievement or goal reached' },
  { type: 'funding', label: 'Funding', description: 'Fundraising news or updates' },
  { type: 'traction', label: 'Traction', description: 'Growth metrics or customer wins' },
  { type: 'team', label: 'Team', description: 'New hires or team changes' },
  { type: 'product', label: 'Product', description: 'Product launches or features' },
  { type: 'announcement', label: 'Announcement', description: 'General news or press' },
];

const VISIBILITY_OPTIONS: { value: UpdateVisibility; label: string; description: string; icon: 'globe' | 'folder' | 'lock' }[] = [
  { value: 'public', label: 'Public', description: 'Visible to all investors', icon: 'globe' },
  { value: 'data_room', label: 'Data Room Access', description: 'Only investors with data room access', icon: 'folder' },
  { value: 'investors_only', label: 'Investors Only', description: 'Only investors who have invested', icon: 'lock' },
];

// Breadcrumbs for navigation
const BREADCRUMBS: Breadcrumb[] = [
  { label: 'Company Profile', href: '/(startup)/company' },
  { label: 'News & Updates' },
];

export default function StartupUpdatesScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const startupProfile = useProfileStore((s) => s.startupProfile);
  const addStartupUpdate = useProfileStore((s) => s.addStartupUpdate);
  const user = useProfileStore((s) => s.user);

  const [showNewUpdateModal, setShowNewUpdateModal] = useState(false);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [showVisibilitySelector, setShowVisibilitySelector] = useState(false);
  const [selectedType, setSelectedType] = useState<UpdateType>('announcement');
  const [selectedVisibility, setSelectedVisibility] = useState<UpdateVisibility>('public');
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [externalLink, setExternalLink] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updates = startupProfile?.updates || [];

  // Get icon for update type
  const getUpdateIcon = (type: UpdateType, size = 18) => {
    switch (type) {
      case 'milestone':
        return <Star size={size} color={colors.success} />;
      case 'funding':
        return <DollarSign size={size} color={colors.success} />;
      case 'traction':
        return <TrendingUp size={size} color={colors.warning} />;
      case 'team':
        return <UserPlus size={size} color={colors.primary[600]} />;
      case 'product':
        return <Rocket size={size} color={colors.primary[600]} />;
      case 'announcement':
        return <Megaphone size={size} color={colors.primary[600]} />;
      default:
        return <Newspaper size={size} color={colors.text.tertiary} />;
    }
  };

  // Get background color for update type
  const getUpdateBgColor = (type: UpdateType) => {
    switch (type) {
      case 'milestone':
      case 'funding':
        return colors.traffic.greenLight;
      case 'traction':
        return colors.amber.light;
      default:
        return colors.primary[50];
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get visibility icon
  const getVisibilityIcon = (visibility: UpdateVisibility, size = 12) => {
    switch (visibility) {
      case 'public':
        return <Globe size={size} color={colors.text.tertiary} />;
      case 'data_room':
        return <FolderOpen size={size} color={colors.warning} />;
      case 'investors_only':
        return <Lock size={size} color={colors.primary[600]} />;
    }
  };

  // Get visibility label
  const getVisibilityLabel = (visibility: UpdateVisibility) => {
    return VISIBILITY_OPTIONS.find((v) => v.value === visibility)?.label || 'Public';
  };

  const handleSubmit = () => {
    if (!title.trim() || !summary.trim()) return;

    setIsSubmitting(true);

    // Add the update
    addStartupUpdate({
      type: selectedType,
      title: title.trim(),
      summary: summary.trim(),
      externalLink: externalLink.trim() || undefined,
      createdBy: user?.name || 'Team Member',
      visibility: selectedVisibility,
    });

    // Reset form
    setTitle('');
    setSummary('');
    setExternalLink('');
    setSelectedType('announcement');
    setSelectedVisibility('public');
    setIsSubmitting(false);
    setShowNewUpdateModal(false);
  };

  const resetForm = () => {
    setTitle('');
    setSummary('');
    setExternalLink('');
    setSelectedType('announcement');
    setSelectedVisibility('public');
    setShowNewUpdateModal(false);
  };

  return (
    <AppLayout title="News & Updates" breadcrumbs={BREADCRUMBS}>
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
          <ScrollView
            className="flex-1"
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ paddingBottom: 100 }}
          >
            {/* Header */}
            <Animated.View
              entering={FadeInDown.delay(50).duration(500)}
              className="px-5 pt-6 pb-4"
            >
              <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
                News & Updates
              </Text>
              <Text style={{ color: colors.text.secondary }} className="text-sm mt-1">
                Share updates with investors to keep them engaged
              </Text>
            </Animated.View>

            {/* Post New Update Button */}
            <Animated.View
              entering={FadeInUp.delay(100).duration(500)}
              className="px-5 mb-6"
            >
              <AnimatedPressable
                onPress={() => setShowNewUpdateModal(true)}
                className="flex-row items-center justify-center gap-2 py-4 rounded-2xl"
                style={{ backgroundColor: colors.primary[600] }}
              >
                <Plus size={20} color="white" />
                <Text className="text-white font-semibold">Post New Update</Text>
              </AnimatedPressable>
            </Animated.View>

            {/* Updates List */}
            <View className="px-5">
              <SectionDivider label="Recent Updates" marginTop={0} marginBottom={12} />
            </View>

            {updates.length === 0 ? (
              <Animated.View
                entering={FadeInUp.delay(150).duration(500)}
                className="px-5"
              >
                <View
                  className="rounded-2xl p-6 items-center"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <View
                    className="w-16 h-16 rounded-full items-center justify-center mb-4"
                    style={{ backgroundColor: colors.primary[50] }}
                  >
                    <Newspaper size={32} color={colors.primary[600]} />
                  </View>
                  <Text style={{ color: colors.text.primary }} className="text-lg font-semibold text-center mb-2">
                    No Updates Yet
                  </Text>
                  <Text style={{ color: colors.text.secondary }} className="text-sm text-center">
                    Post your first update to share milestones, traction, and news with interested investors.
                  </Text>
                </View>
              </Animated.View>
            ) : (
              <Animated.View
                entering={FadeInUp.delay(150).duration(500)}
                className="px-5"
              >
                <View
                  className="rounded-2xl overflow-hidden"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  {updates
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map((update, index) => (
                      <View
                        key={update.id}
                        className={`p-4 ${index < updates.length - 1 ? 'border-b' : ''}`}
                        style={{ borderColor: colors.border.light }}
                      >
                        <View className="flex-row items-start gap-3">
                          <View
                            className="w-10 h-10 rounded-full items-center justify-center"
                            style={{ backgroundColor: getUpdateBgColor(update.type) }}
                          >
                            {getUpdateIcon(update.type)}
                          </View>
                          <View className="flex-1">
                            <Text style={{ color: colors.text.primary }} className="font-semibold mb-1">
                              {update.title}
                            </Text>
                            <Text style={{ color: colors.text.secondary }} className="text-sm mb-2" numberOfLines={2}>
                              {update.summary}
                            </Text>
                            <View className="flex-row items-center gap-3">
                              <View className="flex-row items-center gap-1">
                                <Clock size={12} color={colors.text.tertiary} />
                                <Text style={{ color: colors.text.tertiary }} className="text-xs">
                                  {formatDate(update.date)}
                                </Text>
                              </View>
                              {update.externalLink && (
                                <View className="flex-row items-center gap-1">
                                  <ExternalLink size={12} color={colors.primary[600]} />
                                  <Text style={{ color: colors.primary[600] }} className="text-xs">
                                    Link
                                  </Text>
                                </View>
                              )}
                              {/* Visibility indicator */}
                              <View className="flex-row items-center gap-1">
                                {getVisibilityIcon(update.visibility)}
                                <Text
                                  style={{
                                    color: update.visibility === 'public'
                                      ? colors.text.tertiary
                                      : update.visibility === 'data_room'
                                        ? colors.warning
                                        : colors.primary[600],
                                  }}
                                  className="text-xs"
                                >
                                  {getVisibilityLabel(update.visibility)}
                                </Text>
                              </View>
                            </View>
                          </View>
                        </View>
                      </View>
                    ))}
                </View>
              </Animated.View>
            )}

            {/* Tips */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(500)}
              className="px-5 mt-6"
            >
              <View
                className="rounded-2xl p-4"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <Text style={{ color: colors.primary[700] }} className="font-semibold mb-2">
                  Tips for Effective Updates
                </Text>
                <View className="gap-2">
                  <View className="flex-row items-start gap-2">
                    <Check size={14} color={colors.primary[600]} className="mt-0.5" />
                    <Text style={{ color: colors.primary[700] }} className="text-sm flex-1">
                      Share concrete metrics and milestones to build credibility
                    </Text>
                  </View>
                  <View className="flex-row items-start gap-2">
                    <Check size={14} color={colors.primary[600]} className="mt-0.5" />
                    <Text style={{ color: colors.primary[700] }} className="text-sm flex-1">
                      Post regularly (2-4 updates a month) to stay top-of-mind
                    </Text>
                  </View>
                  <View className="flex-row items-start gap-2">
                    <Check size={14} color={colors.primary[600]} className="mt-0.5" />
                    <Text style={{ color: colors.primary[700] }} className="text-sm flex-1">
                      Include external links to press coverage when available
                    </Text>
                  </View>
                </View>
              </View>
            </Animated.View>
          </ScrollView>
        </SafeAreaView>

        {/* New Update Modal */}
        <Modal
          visible={showNewUpdateModal}
          animationType={isWeb ? 'fade' : 'slide'}
          presentationStyle={isWeb ? undefined : 'pageSheet'}
          transparent={isWeb}
          onRequestClose={resetForm}
        >
          <View style={isWeb ? { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' } : { flex: 1 }}>
          <View style={isWeb ? { width: '100%', maxWidth: 520, maxHeight: '85%', borderRadius: 16, overflow: 'hidden' } : { flex: 1 }}>
          <SafeAreaView edges={isWeb ? [] : ['top', 'bottom']} className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
            <KeyboardAvoidingView
              behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
              className="flex-1"
            >
              {/* Modal Header */}
              <View
                className="flex-row items-center justify-between px-5 py-4"
                style={{ backgroundColor: colors.surface.primary, borderBottomWidth: 1, borderBottomColor: colors.border.light }}
              >
                <AnimatedPressable
                  onPress={resetForm}
                  className="w-10 h-10 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.surface.secondary }}
                >
                  <X size={24} color={colors.text.primary} />
                </AnimatedPressable>
                <Text style={{ color: colors.text.primary }} className="text-lg font-semibold">
                  New Update
                </Text>
                <AnimatedPressable
                  onPress={handleSubmit}
                  disabled={!title.trim() || !summary.trim() || isSubmitting}
                  className="px-4 py-2 rounded-lg"
                  style={{
                    backgroundColor: title.trim() && summary.trim() ? colors.primary[600] : colors.light[300],
                  }}
                >
                  <Text
                    className="font-semibold"
                    style={{ color: title.trim() && summary.trim() ? 'white' : colors.text.tertiary }}
                  >
                    Post
                  </Text>
                </AnimatedPressable>
              </View>

              {/* Modal Content — keyboard-aware. The summary and body fields
                  sit below the type selector, so on a phone they were behind
                  the keyboard as soon as they took focus. Layout unchanged. */}
              <KeyboardAwareBody className="flex-1" contentContainerStyle={{ padding: 20 }}>
                {/* Update Type Selector */}
                <View className="mb-5">
                  <Text style={{ color: colors.text.primary }} className="font-medium mb-2">
                    Update Type
                  </Text>
                  <AnimatedPressable
                    onPress={() => setShowTypeSelector(!showTypeSelector)}
                    className="flex-row items-center justify-between p-4 rounded-xl"
                    style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                  >
                    <View className="flex-row items-center gap-3">
                      <View
                        className="w-8 h-8 rounded-lg items-center justify-center"
                        style={{ backgroundColor: getUpdateBgColor(selectedType) }}
                      >
                        {getUpdateIcon(selectedType, 16)}
                      </View>
                      <Text style={{ color: colors.text.primary }} className="font-medium capitalize">
                        {UPDATE_TYPES.find((t) => t.type === selectedType)?.label}
                      </Text>
                    </View>
                    <ChevronDown size={20} color={colors.text.tertiary} />
                  </AnimatedPressable>

                  {/* Type Options */}
                  {showTypeSelector && (
                    <View
                      className="mt-2 rounded-xl overflow-hidden"
                      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                    >
                      {UPDATE_TYPES.map((typeOption, index) => (
                        <AnimatedPressable
                          key={typeOption.type}
                          onPress={() => {
                            setSelectedType(typeOption.type);
                            setShowTypeSelector(false);
                          }}
                          className={`flex-row items-center gap-3 p-4 ${
                            index < UPDATE_TYPES.length - 1 ? 'border-b' : ''
                          }`}
                          style={{ borderColor: colors.border.light }}
                        >
                          <View
                            className="w-8 h-8 rounded-lg items-center justify-center"
                            style={{ backgroundColor: getUpdateBgColor(typeOption.type) }}
                          >
                            {getUpdateIcon(typeOption.type, 16)}
                          </View>
                          <View className="flex-1">
                            <Text style={{ color: colors.text.primary }} className="font-medium">
                              {typeOption.label}
                            </Text>
                            <Text style={{ color: colors.text.tertiary }} className="text-xs">
                              {typeOption.description}
                            </Text>
                          </View>
                          {selectedType === typeOption.type && (
                            <Check size={18} color={colors.primary[600]} />
                          )}
                        </AnimatedPressable>
                      ))}
                    </View>
                  )}
                </View>

                {/* Visibility Selector */}
                <View className="mb-5">
                  <Text style={{ color: colors.text.primary }} className="font-medium mb-2">
                    Visibility
                  </Text>
                  <AnimatedPressable
                    onPress={() => setShowVisibilitySelector(!showVisibilitySelector)}
                    className="flex-row items-center justify-between p-4 rounded-xl"
                    style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                  >
                    <View className="flex-row items-center gap-3">
                      <View
                        className="w-8 h-8 rounded-lg items-center justify-center"
                        style={{
                          backgroundColor: selectedVisibility === 'public'
                            ? colors.light[200]
                            : selectedVisibility === 'data_room'
                              ? colors.amber.light
                              : colors.primary[50],
                        }}
                      >
                        {selectedVisibility === 'public' && <Globe size={16} color={colors.text.tertiary} />}
                        {selectedVisibility === 'data_room' && <FolderOpen size={16} color={colors.warning} />}
                        {selectedVisibility === 'investors_only' && <Lock size={16} color={colors.primary[600]} />}
                      </View>
                      <Text style={{ color: colors.text.primary }} className="font-medium">
                        {VISIBILITY_OPTIONS.find((v) => v.value === selectedVisibility)?.label}
                      </Text>
                    </View>
                    <ChevronDown size={20} color={colors.text.tertiary} />
                  </AnimatedPressable>

                  {/* Visibility Options */}
                  {showVisibilitySelector && (
                    <View
                      className="mt-2 rounded-xl overflow-hidden"
                      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                    >
                      {VISIBILITY_OPTIONS.map((option, index) => (
                        <AnimatedPressable
                          key={option.value}
                          onPress={() => {
                            setSelectedVisibility(option.value);
                            setShowVisibilitySelector(false);
                          }}
                          className={`flex-row items-center gap-3 p-4 ${
                            index < VISIBILITY_OPTIONS.length - 1 ? 'border-b' : ''
                          }`}
                          style={{ borderColor: colors.border.light }}
                        >
                          <View
                            className="w-8 h-8 rounded-lg items-center justify-center"
                            style={{
                              backgroundColor: option.value === 'public'
                                ? colors.light[200]
                                : option.value === 'data_room'
                                  ? colors.amber.light
                                  : colors.primary[50],
                            }}
                          >
                            {option.icon === 'globe' && <Globe size={16} color={colors.text.tertiary} />}
                            {option.icon === 'folder' && <FolderOpen size={16} color={colors.warning} />}
                            {option.icon === 'lock' && <Lock size={16} color={colors.primary[600]} />}
                          </View>
                          <View className="flex-1">
                            <Text style={{ color: colors.text.primary }} className="font-medium">
                              {option.label}
                            </Text>
                            <Text style={{ color: colors.text.tertiary }} className="text-xs">
                              {option.description}
                            </Text>
                          </View>
                          {selectedVisibility === option.value && (
                            <Check size={18} color={colors.primary[600]} />
                          )}
                        </AnimatedPressable>
                      ))}
                    </View>
                  )}
                </View>

                {/* Title */}
                <View className="mb-5">
                  <Text style={{ color: colors.text.primary }} className="font-medium mb-2">
                    Title *
                  </Text>
                  <TextInput
                    value={title}
                    onChangeText={setTitle}
                    placeholder="e.g., Reached 1,000 Active Users"
                    placeholderTextColor={colors.text.tertiary}
                    className="p-4 rounded-xl"
                    style={{
                      backgroundColor: colors.surface.primary,
                      borderWidth: 1,
                      borderColor: colors.border.light,
                      color: colors.text.primary,
                      fontSize: 16,
                    }}
                  />
                </View>

                {/* Summary */}
                <View className="mb-5">
                  <Text style={{ color: colors.text.primary }} className="font-medium mb-2">
                    Summary *
                  </Text>
                  <TextInput
                    value={summary}
                    onChangeText={setSummary}
                    placeholder="Write a brief summary of your update..."
                    placeholderTextColor={colors.text.tertiary}
                    multiline
                    numberOfLines={4}
                    className="p-4 rounded-xl"
                    style={{
                      backgroundColor: colors.surface.primary,
                      borderWidth: 1,
                      borderColor: colors.border.light,
                      color: colors.text.primary,
                      fontSize: 16,
                      minHeight: 120,
                      textAlignVertical: 'top',
                    }}
                  />
                  <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1">
                    Keep it concise. 2-3 sentences work best.
                  </Text>
                </View>

                {/* External Link */}
                <View className="mb-5">
                  <Text style={{ color: colors.text.primary }} className="font-medium mb-2">
                    External Link (Optional)
                  </Text>
                  <View
                    className="flex-row items-center rounded-xl overflow-hidden"
                    style={{
                      backgroundColor: colors.surface.primary,
                      borderWidth: 1,
                      borderColor: colors.border.light,
                    }}
                  >
                    <View className="px-3">
                      <Link size={18} color={colors.text.tertiary} />
                    </View>
                    <TextInput
                      value={externalLink}
                      onChangeText={setExternalLink}
                      placeholder="https://..."
                      placeholderTextColor={colors.text.tertiary}
                      autoCapitalize="none"
                      keyboardType="url"
                      className="flex-1 py-4 pr-4"
                      style={{
                        color: colors.text.primary,
                        fontSize: 16,
                      }}
                    />
                  </View>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1">
                    Link to press coverage, blog post, or external announcement
                  </Text>
                </View>
              </KeyboardAwareBody>
            </KeyboardAvoidingView>
          </SafeAreaView>
          </View>
          </View>
        </Modal>
      </View>
    </AppLayout>
  );
}
